import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import axios from '@/api/axios'

const STORAGE_KEY = 'rbac_permissions_v1'

export const usePermissionsStore = defineStore('permissions', () => {
  // State
  const codes = ref<string[]>([])
  const lastFetch = ref<number | null>(null)
  const loading = ref(false)

  // Getters
  const has = (code: string) => codes.value.includes(code)

  const hasAny = (targetCodes: string[]) =>
    Array.isArray(targetCodes) && targetCodes.some((c) => has(c))

  const hasAll = (targetCodes: string[]) =>
    Array.isArray(targetCodes) && targetCodes.every((c) => has(c))

  const permissionSet = computed(() => new Set(codes.value))

  // Actions
  function setCodes(newCodes: string[]) {
    codes.value = [...new Set(newCodes)]
  }

  function persist() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(codes.value))
    } catch {
      // No-op: localStorage puede estar bloqueado en modo privado/incógnito
    }
  }

  function hydrate() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) {
        const parsed = JSON.parse(raw) as string[]
        setCodes(parsed)
      }
    } catch {
      codes.value = []
    }
  }

  function clear() {
    codes.value = []
    lastFetch.value = null
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      // No-op
    }
  }

  async function fetchPermissions() {
    loading.value = true
    try {
      // Backend devuelve MePermissionsResponse {permissions: string[]}
      const response = await axios.get<{ permissions: string[] }>('/api/v1/me/permissions')
      setCodes(response.data?.permissions || [])
      lastFetch.value = Date.now()
      persist()
    } catch (error: any) {
      // Si falla, mantener lo que haya en memoria/localStorage para evitar bloqueos transitorios
      throw error
    } finally {
      loading.value = false
    }
  }

  // Helpers directos para uso en componentes y guards
  const can = (code: string | undefined | null) => !!code && has(code)

  const canAny = (targetCodes: string[] | string | undefined | null) => {
    if (!targetCodes) return true
    const arr = Array.isArray(targetCodes) ? targetCodes : [targetCodes]
    return hasAny(arr)
  }

  const canAll = (targetCodes: string[] | string | undefined | null) => {
    if (!targetCodes) return true
    const arr = Array.isArray(targetCodes) ? targetCodes : [targetCodes]
    return hasAll(arr)
  }

  return {
    codes,
    lastFetch,
    loading,
    permissionSet,
    has,
    hasAny,
    hasAll,
    can,
    canAny,
    canAll,
    fetchPermissions,
    clear,
    hydrate,
    setCodes,
    persist,
  }
})
